// GPT-Live に渡す instructions・tools・セッションの設定・読み上げの依頼文
// (spec 2026-09-14 §3.2 / §3.3)。
//
// **文面は spike の probe を逐語で移したもの** (docs/research/gpt-live/name-probe.py /
// answer-probe.py)。思いつきで書き換えないこと — n=30 での prompt の調整は過剰適合する
// (2026-08-18 に 1 行足して 67%→63% に落とした実測)。spike から変えたのは次の 2 点で、
// **どちらも未計測** (spec §3.3):
//   1. 本編の live 側の「進行の方針」(spike の所見 6 への手当て)
//   2. 本編の backend 側を設問 1 問から一覧に広げたこと (1 セッションで 5 問を回すため)
//
// **DOM に触れない** — functions/api/live/session.ts が import してセッションを組む。

import { TTS_PHRASES } from "../audio/ttsPhrases";
import { ANSWER_TOOLS } from "./realtimeInstructions";
import { LIVE_BACKEND_MODEL, LIVE_MODEL, LIVE_VOICE } from "./liveModel";

export type LivePurpose = "preflight" | "survey";
export type LiveQuestion = { id: string; text: string };

/**
 * ブラウザ (信頼しない側) の data channel に送らせてよいイベント (spec L5)。
 * **session.update は入れない** — 許すと delegation.responses.model を高いモデルに
 * 差し替えられる。ここに足すときは、ブラウザから何ができるようになるかを先に書くこと。
 */
export const LIVE_ALLOWED_CLIENT_EVENTS = [
  "session.instructions.append",
  "response.item.create",
  "response.create",
  "session.close",
];

/**
 * backend の 1 応答の上限 (spec L6)。spike の実測は平均 46 tokens で、回答の全文を
 * record_answer に写しても 1,000 に届かない。無認証の endpoint から使われたときの費用を抑える。
 */
export const LIVE_BACKEND_MAX_OUTPUT_TOKENS = 2000;

// ---- 事前チェック (name-probe.py の COMMON + DELEGATION_LIVE を逐語で。2026-09-14 の実機を受けて
// 「任せるとき / 任せないとき」に 1 句ずつ足した — 名乗った直後 (復唱の前) に任せ、「はい」の後に
// もう任せずに止まったため。足した後の効き目は測っていない (止まらないことはアプリの網が担保する)) ----
const PREFLIGHT_LIVE_INSTRUCTIONS = `あなたは「アンケートの案内役」です。日本語の音声アンケートの事前確認を担当します。
落ち着いた丁寧な口調で、はっきり話してください。必ず日本語だけで話してください。

相槌の方針: 相槌は控えめにする。相手が話している間は遮らない。
割り込みの方針: 相手が話し始めたら、話すのをやめて聞く。
読み上げの方針: 読み上げるよう頼まれた文は、一字一句そのまま読み上げる。言い換え・要約・前置き・付け足しをしない。

氏名の聞き取り:
- 相手が名乗ったら、聞き取った氏名をカタカナの読みで一度だけ復唱する。「〜さん、ですね。」の形にする。
- 氏名は未知の固有名詞である。知っている名前や有名人の名前に寄せて直さず、聞こえた音をそのまま写す。
- 語頭の子音 (カ行・タ行・ハ行・ナ行・マ行) は取り違えやすい。聞こえたとおりに言う。
- 違うと言われたら、もう一度お名前を伺う。

任せる方針 (delegation):
バックエンドのツール:
- 氏名の記録: 確認の済んだ氏名を記録する。
バックエンドに任せるとき:
- 復唱した氏名を、相手が肯定したとき (「はい」「そうです」など)。前に一度任せていても、肯定されたら改めて任せる。
バックエンドに任せないとき:
- 相手がまだ名乗っていないとき。名乗った直後 (復唱する前)。復唱への返事を待っているとき。
記録が済んだと言う前に、必ずバックエンドに任せる。記録の結果を推測で言わない。`;

// name-probe.py の BACKEND_INSTRUCTIONS を逐語で
const PREFLIGHT_BACKEND_INSTRUCTIONS = `## 音声会話の文脈
あなたは、音声で行うアンケートの事前確認で、案内役を手伝う記録係である。会話の文字起こしには
誤り・言いかけ・言い直しが含まれる。最新の文脈を使う。

## 業務の指示
- 相手が名乗り、案内役の復唱を肯定した氏名を confirm_name で記録する。
- 氏名は必ずカタカナで書く。ひらがな・漢字・ローマ字を混ぜない。姓と名の間に空白を入れない。
- 長音は「ー」、促音は「ッ」で書く。「です」「といいます」などの語尾は含めない。
- 氏名は未知の固有名詞である。知っている名前に寄せて直さない。
- 姓と名の両方が取れていれば full を true にする。
- 氏名がまったく判別できないときだけ need_repeat を呼ぶ。

## 結果の返し方
記録したかどうかだけを一言で返す。記録していないのに記録したと言わない。`;

/** 事前チェックの backend の tools (name-probe.py の TOOLS を逐語で) */
export const LIVE_NAME_TOOLS: unknown[] = [
  {
    type: "function",
    name: "confirm_name",
    description: "確認の済んだ氏名をカタカナで記録する。",
    parameters: {
      type: "object",
      properties: {
        katakana: {
          type: "string",
          description: "氏名の読み。カタカナのみ。姓と名の間に空白を入れない。",
        },
        full: { type: "boolean", description: "姓と名の両方が取れたか" },
      },
      required: ["katakana", "full"],
    },
  },
  {
    type: "function",
    name: "need_repeat",
    description: "氏名がまったく聞き取れないときに呼ぶ。",
    parameters: {
      type: "object",
      properties: { reason: { type: "string" } },
      required: ["reason"],
    },
  },
];

// ---- 本編 (answer-probe.py の COMMON + DELEGATION_LIVE を逐語で。「進行の方針」の 1 行だけ足した) ----
const SURVEY_LIVE_INSTRUCTIONS = `あなたは「アンケートの聞き手」です。日本語の業務振り返りアンケートで、設問を読み上げて回答を伺います。
落ち着いた丁寧な口調で、はっきり話してください。必ず日本語だけで話してください。

相槌の方針: 相槌は控えめにする。相手が話している間や、考えている間の沈黙は遮らない。
割り込みの方針: 相手が話し始めたら、話すのをやめて聞く。
読み上げの方針: 読み上げるよう頼まれた設問文は、一字一句そのまま読み上げる。言い換え・要約・前置き・付け足しをしない。
回答の受け取り: 内容を評価しない。詳しく聞き直さない。掘り下げない。
進行の方針: 次の設問はこちらから頼む。自分から次の設問へ進んだり、アンケートを締めくくったりしない。

任せる方針 (delegation):
バックエンドのツール:
- 回答の記録: 設問への相手の回答を、相手の言葉のまま記録する。聞き直しが要るかも判断する。
バックエンドに任せるとき:
- 相手が設問に答え終えたとき。短い答えや「特にありません」「わかりません」も含む。
- 相手が聞き返したとき、または設問と関係のないことを言ったとき。
バックエンドに任せないとき:
- 相手がまだ話している途中のとき。
記録が済んだと言う前に、必ずバックエンドに任せる。記録の結果を推測で言わない。任せたら、短くお礼を言って待つ。`;

/**
 * 本編の backend の instructions。answer-probe.py の backend_instructions を、設問 1 問から
 * **一覧**に広げたもの (1 セッションで 5 問を回すため)。question_id は「直前に読み上げた設問」
 * とさせるが、**記録をどの設問に付けるかはアプリが決める** (spec §3.5) ので、ここがずれても
 * 回答はずれない。
 */
export function buildSurveyBackendInstructions(questions: LiveQuestion[]): string {
  const list = questions.map((q) => `- 設問 ID: ${q.id} / 設問文: ${q.text}`).join("\n");
  return `## 音声会話の文脈
あなたは、音声で行う業務振り返りアンケートで、聞き手を手伝う記録係である。会話の文字起こしには
誤り・言いかけ・言い直しが含まれる。最新の文脈を使う。

## 業務の指示
このアンケートの設問は次のとおり。聞き手が 1 問ずつ読み上げる。
${list}

- 相手の返答を record_answer で記録する。question_id には、直前に聞き手が読み上げた設問の ID をそのまま入れる。
  value には相手が言った内容を、要約せずそのままの言葉で入れる。
- 相手が何か答えたなら、それは回答である。短くても、言い淀んでも、途中で言い直しても、
  設問と少しずれていても、本人の言葉なら record_answer を呼ぶ。
- 「特にありません」「わかりません」も正当な回答である。record_answer を呼ぶこと。
- need_repeat を呼ぶのは次の 3 つだけ:
  言葉が 1 つも聞き取れなかった (雑音・無音) /「もう一度言ってください」と聞き返された /
  設問と関係のない質問をされた (「今何時ですか」など)

## 結果の返し方
記録したかどうかだけを一言で返す。記録していないのに記録したと言わない。`;
}

export type LiveSessionConfig = {
  model: string;
  instructions: string;
  audio: { output: { voice: string } };
  delegation: {
    type: "responses";
    responses: {
      model: string;
      instructions: string;
      tools: unknown[];
      tool_choice: "auto";
      parallel_tool_calls: false;
      max_output_tokens: number;
    };
  };
  client: { data_channel: { allowed_client_events: string[] } };
  store: false;
};

/**
 * POST /v1/live/sessions の session (spec §3.2)。**instructions・backend・tools・声・モデルは
 * すべてここで決める** — ブラウザからは選ばせない (L5)。
 */
export function buildLiveSessionConfig(
  purpose: LivePurpose,
  questions: LiveQuestion[] = []
): LiveSessionConfig {
  const survey = purpose === "survey";
  return {
    model: LIVE_MODEL,
    instructions: survey ? SURVEY_LIVE_INSTRUCTIONS : PREFLIGHT_LIVE_INSTRUCTIONS,
    audio: { output: { voice: LIVE_VOICE } },
    delegation: {
      type: "responses",
      responses: {
        model: LIVE_BACKEND_MODEL,
        instructions: survey
          ? buildSurveyBackendInstructions(questions)
          : PREFLIGHT_BACKEND_INSTRUCTIONS,
        tools: survey ? ANSWER_TOOLS : LIVE_NAME_TOOLS,
        tool_choice: "auto",
        parallel_tool_calls: false,
        max_output_tokens: LIVE_BACKEND_MAX_OUTPUT_TOKENS,
      },
    },
    client: { data_channel: { allowed_client_events: [...LIVE_ALLOWED_CLIENT_EVENTS] } },
    store: false,
  };
}

// ---- 読み上げの依頼文 (session.instructions.append。1 回 500 tokens まで) ----
// **頼むだけで、その通りに言う保証は無い** ("This requests the wording; it does not guarantee
// exact delivery.")。spike ではこの文面で設問 30/30・案内 30/30・チュートリアル 10/10 を読んだ。

/** 設問を読ませる (answer-probe.py の依頼文) */
export function buildQuestionAppend(text: string): string {
  return `今すぐ次の設問文を、一字一句そのまま読み上げてください。読み上げたら、相手の回答を黙って待ってください。\n\n設問文: ${text}`;
}

/** 氏名の案内を読ませる (name-probe.py の 1 番目の依頼文) */
export function buildNamePromptAppend(): string {
  return `今すぐ次の文を、一字一句そのまま読み上げてください。読み上げたら、相手が名乗るのを黙って待ってください。\n\n${TTS_PHRASES.namePrompt}`;
}

/** 案内を読ませる (name-probe.py の 5 番目の依頼文)。1 文のときは「次の文を」 */
export function buildNarrationAppend(lines: string[]): string {
  if (lines.length === 1) {
    return `今すぐ次の文を、一字一句そのまま読み上げてください。前置きや付け足しをしないでください。\n\n${lines[0]}`;
  }
  const numbered = lines.map((l, i) => `${i + 1}. ${l}`).join("\n");
  return `今すぐ次の ${lines.length} つの文を、書かれている順に、一字一句そのまま読み上げてください。前置きや付け足しをしないでください。\n\n${numbered}`;
}

/** 黙っている相手に促す (2.1 の SILENCE_PROMPT を追記の形にしたもの。文言は cascade と同じ) */
export function buildSilenceAppend(): string {
  return `相手がしばらく黙っています。今すぐ次の文を、一字一句そのまま読み上げてください。読み上げる以外のことは何も言わないでください。\n\n${TTS_PHRASES.silence0}`;
}

/** アプリが backend に直接頼む文 (answer-probe.py の ask_backend。app モードで 8/8 を記録した形) */
export function buildAnswerFallbackMessage(questionId: string, heard: string): string {
  return `設問 ${questionId} への相手の回答の文字起こし: ${heard}`;
}

/**
 * 氏名の二重の網でアプリが backend に直接頼む文 (spec §9.4)。backend の指示は「相手が名乗り、
 * 案内役の復唱を肯定した氏名を confirm_name で記録する」なので、復唱と返事をそのまま渡す
 */
export function buildNameConfirmMessage(recitation: string, reply: string): string {
  return `案内役が「${recitation}」と復唱し、相手は「${reply}」と答えた。`;
}
